/* eslint-disable react-hooks/exhaustive-deps */
import { useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { TagEnum } from '@orijinworks/frontend-commons';
import { getDMFeeds, getFeedMessages, createMessage, getFeedById, markFeedAsRead } from '../../services/Feed.service';
import {
  transformApiConversationToDisplay,
  transformSocketEventToConversation,
  transformSocketEventToMessage,
  generateFeedSettings,
  generateEditorProps,
  checkUserRoleType,
} from '../../mappers/feedUtility';
import { PAGINATION_CONSTANTS, FEED_TYPE, MESSAGE_TYPE, TAB_NAMES } from '../../constants';
import { ROLES } from '../../../../../constants';
import { useFeedMessagesMenuHook } from '../../../../shared/hooks';


/**
 * Hook for managing Direct Messages functionality
 * Gets user data directly from localStorage
 */
export const useDirectMessagesHook = (onBackToOriginalTab, isActive = false, decrementUnreadCount, activeTab) => {
  // State
  // Get user details from Redux store
  const { userDetail } = useSelector((state) => state.app || {});
  const currentUserKey = userDetail?.userId;
  const [conversations, setConversations] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [conversationMessages, setConversationMessages] = useState([]);
  const [feedSettings, setFeedSettings] = useState([]);
  const [editorProps, setEditorProps] = useState(null);
  const [isConversationsLoading, setIsConversationsLoading] = useState(false);
  const [isMessagesLoading, setIsMessagesLoading] = useState(false);
  const [isMessagesLoadingMore, setIsMessagesLoadingMore] = useState(false);
  const [hasLoadedInitialConversations, setHasLoadedInitialConversations] = useState(false);
  const [feedDetails, setFeedDetails] = useState(null);
  const [totalConversationCount, setTotalConversationCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [currentMessagePage, setCurrentMessagePage] = useState(1);
  const [hasMoreMessages, setHasMoreMessages] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [hasMore, setHasMore] = useState(true);
  const [showNewMessagesIndicator, setShowNewMessagesIndicator] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(true);

  const isMountedRef = useRef(true);
  const loadingConversationRef = useRef(null);
  const selectedConversationRef = useRef(selectedConversation);
  const messageViewRef = useRef(null);
  const searchDebounceTimer = useRef(null);
  const conversationListRef = useRef(conversations);
  const hasSentMessageInSessionRef = useRef(false);



  const showNewMessageBtn = useMemo(() => {
    return checkUserRoleType([ROLES.SUPER_ADMIN, ROLES.STAFF, ROLES.MESSAGING_ONLY, ROLES.TEACHER]);
  }, []);

  // Hook for message menu options and modal handlers
  const { menuProps, readReceiptsModal } = useFeedMessagesMenuHook();

  const clearConversationState = useCallback(() => {
    setConversationMessages([]);
    setFeedSettings([]);
    setEditorProps(null);
    setCurrentMessagePage(1);
    setHasMoreMessages(false);
    setShowNewMessagesIndicator(false);
    setIsAtBottom(true);
  }, []);

  const updateConversationsState = useCallback((newConversations, shouldReset) => {
    if (shouldReset) {
      setConversations(newConversations);
    } else {
      setConversations((prev) => [...prev, ...newConversations]);
    }
  }, []);

  const markConversationAsRead = async (conversationId) => {
    try {
      const response = await markFeedAsRead(conversationId);
      const messagesMarkedAsRead = response?.data?.messagesMarkedAsRead || 0;
      
      setConversations((prev) =>
        prev.map((conv) => {
          if (conv.id === conversationId) {
            return {
              ...conv,
              isNew: false,
              unreadCount: 0,
              tags: conv.tags?.filter((tag) => tag.type !== TagEnum.New) || [],
            };
          }
          return conv;
        })
      );

      if (decrementUnreadCount && messagesMarkedAsRead > 0) {
        decrementUnreadCount(FEED_TYPE.DM, messagesMarkedAsRead);
      }      
    } catch (error) {
      console.error('[DirectMessages] Failed to mark conversation as read:', error);
      return 0;
    }
  }

  const addMessageToCurrentConversation = useCallback((newMessage) => {
    // Use setConversationMessages directly to ensure menuProps are properly included
    // This matches the pattern used in sendMessage for consistency
    setConversationMessages((prev) => [...prev, newMessage]);
  }, []);

  const updateExistingConversation = useCallback((existingConv, socketData, isCurrentConv) => {
    const tags = isCurrentConv
      ? existingConv.tags?.filter((tag) => tag.type !== TagEnum.New) || []
      : [...(existingConv.tags || []), { title: 'New', type: TagEnum.New }].filter(
        (tag, index, arr) => arr.findIndex((t) => t.type === tag.type) === index
      );

    return {
      ...existingConv,
      timestamp: socketData.timestamp,
      description: socketData.body,
      isNew: !isCurrentConv,
      unreadCount: isCurrentConv ? 0 : (existingConv.unreadCount || 0) + 1,
      tags,
    };
  }, []);

  // Send message
  const sendMessage = useCallback(
    async (messageContent) => {
      if (!selectedConversation?.id || !messageContent || !messageContent.trim()) {
        console.error('[DirectMessages] No conversation selected for sending message');
        return;
      }
      try {
        const messageData = {
          body: messageContent,
          messageType: MESSAGE_TYPE.TEXT,
        };
        const response = await createMessage(selectedConversation.id, messageData);
        setConversationMessages((prev) => [...prev, transformSocketEventToMessage(response.data, currentUserKey, menuProps)]);
        // Clear alertProps after successful message send
        if (editorProps?.alertProps) {
          setEditorProps(prevProps => ({
            ...prevProps,
            alertProps: undefined
          }));
        }
        hasSentMessageInSessionRef.current = true;
        return response.data;
      } catch (error) {
        console.error('[DirectMessages] Failed to send message:', error);
      } finally {
        // When user sends a message, scroll to bottom and hide indicator
        setTimeout(() => {
          messageViewRef?.current?.scrollToBottom();
          setIsAtBottom(true);
          setShowNewMessagesIndicator(false);
        }, 100);
      }
    },
    [selectedConversation, editorProps, currentUserKey, menuProps]
  );

  // Helper function to scroll to bottom and update related state
  const scrollToBottomAndUpdateState = useCallback(() => {
    if (messageViewRef?.current) {
      setTimeout(() => {
        messageViewRef.current?.scrollToBottom();
        setIsAtBottom(true);
        setShowNewMessagesIndicator(false);
      }, 100);
    }
  }, []);

  // Helper function to handle initial page load (page 1)
  const handleInitialPageLoad = useCallback((rawMessages) => {
    setConversationMessages(rawMessages.reverse());
    scrollToBottomAndUpdateState();
  }, [scrollToBottomAndUpdateState]);

  // Helper function to handle pagination (page > 1)
  const handlePaginationLoad = useCallback((rawMessages) => {
    setConversationMessages((prev) => {
      const reversedNewMessages = rawMessages.reverse();
      return [...reversedNewMessages, ...prev];
    });
    setIsAtBottom(false);
  }, []);

  // Helper function to update pagination state
  const updatePaginationState = useCallback((page, hasMore) => {
    setCurrentMessagePage(page);
    setHasMoreMessages(hasMore || false);
  }, []);

  const loadMessagesAsync = useCallback(
    async (conversationId, page = 1) => {
      const messagesResponse = await getFeedMessages(conversationId, {
        page,
        size: PAGINATION_CONSTANTS.DEFAULT_PAGE_SIZE,
        currentUserKey,
        menuProps,
      });

      if (!isMountedRef.current) {
        return messagesResponse;
      }

      const rawMessages = messagesResponse.data || [];
      
      if (page === 1) {
        handleInitialPageLoad(rawMessages);
      } else {
        handlePaginationLoad(rawMessages);
      }
      
      updatePaginationState(page, messagesResponse.hasMore);

      return messagesResponse;
    },
    [currentUserKey, menuProps, handleInitialPageLoad, handlePaginationLoad, updatePaginationState]
  );

  const loadFeedDetailsAsync = useCallback(async (conversationId) => {
    const feedDetailsResponse = await getFeedById(conversationId);

    if (isMountedRef.current) {
      const feedDetails = feedDetailsResponse.data || null;
      return feedDetails;
    }

    return feedDetailsResponse.data;
  }, []);

  const refreshFeedDetails = useCallback(
    async (conversationId) => {
      if (!conversationId) return;

      try {
        const feedDetails = await loadFeedDetailsAsync(conversationId);
        if (feedDetails) {
          handleFeedDetailsLoaded(feedDetails);
        }
      } catch (error) {
        console.error('[DirectMessages] Failed to refresh feed details:', error);
      }
    },
    [loadFeedDetailsAsync]
  );

  const handleFeedDetailsLoaded = useCallback(
    (feedDetails) => {
      if (!isMountedRef.current || !feedDetails) return;

      const generatedSettings = generateFeedSettings(feedDetails);
      const generatedEditorProps = generateEditorProps(feedDetails, sendMessage);
      setFeedSettings(generatedSettings);
      setEditorProps(generatedEditorProps);
      setFeedDetails(feedDetails);
    },
    [sendMessage]
  );

  const handleFeedDetailsError = useCallback((error) => {
    console.error('[DirectMessages] Failed to load feed details:', error);
  }, []);

  const openConversation = useCallback(
    async (conversationData) => {
      const conversationId = conversationData.id;
      if (!conversationId) {
        console.error('[DirectMessages] Invalid conversation data:', conversationData);
        return;
      }

      if (loadingConversationRef.current === conversationId) {
        return;
      }

      loadingConversationRef.current = conversationId;

      setSelectedConversation(conversationData);
      setIsMessagesLoading(true);
      clearConversationState();
      setCurrentMessagePage(1);
      setHasMoreMessages(false);
      
      // Mark as read and get the actual count from API response
      if (conversationData.isNew) {
        await markConversationAsRead(conversationId);
        // Only reload if there's no active search to preserve search results
        if (!searchQuery) {
          loadConversations(1, true);
        }
      }
      
      const messagesPromise = loadMessagesAsync(conversationId)
        .then(() => {
          isMountedRef.current && setIsMessagesLoading(false);
        })
        .catch((error) => {
          isMountedRef.current && setIsMessagesLoading(false);
        });
      const detailsPromise = loadFeedDetailsAsync(conversationId)
        .then(handleFeedDetailsLoaded)
        .catch(handleFeedDetailsError);

      Promise.allSettled([messagesPromise, detailsPromise]).finally(() => {
        if (isMountedRef.current) {
          loadingConversationRef.current = null;
        }
      });
    },
    [
      loadMessagesAsync,
      loadFeedDetailsAsync,
      clearConversationState,
      handleFeedDetailsLoaded,
      handleFeedDetailsError,
      decrementUnreadCount
    ]
  );

  const transformConversationsForDisplay = useCallback(
    (conversations) => {
      return conversations.map((conversationData) => {
        const displayConversation = transformApiConversationToDisplay(conversationData, currentUserKey, 'dm');
        return {
          ...displayConversation,
          onFeedSelectHandler: openConversation,
          onViewFullHandler: openConversation,
        };
      });
    },
    [currentUserKey, openConversation]
  );

  const loadConversations = useCallback(
    async (page = 1, shouldReset = false) => {
      if (!isMountedRef.current) return;

      setIsConversationsLoading(true);
      try {
        const response = await getDMFeeds({
          page,
          size: PAGINATION_CONSTANTS.DEFAULT_DM_PAGE_SIZE,
          currentUserKey,
          searchTerm: searchQuery
        });
        const { content = [], totalElements = 0, hasMore: responseHasMore = false } = response.data || {};

        if (content.length > 0) {
          const transformedConversations = transformConversationsForDisplay(content);
          updateConversationsState(transformedConversations, shouldReset);
        } else if (shouldReset) {
          setConversations([]);
        }

        setTotalConversationCount(totalElements);
        setHasLoadedInitialConversations(true);
        setHasMore(responseHasMore);
      } catch (error) {
        setHasLoadedInitialConversations(true); // Add this line such that infinite loop does not trigger for fetching again
        console.error('[DirectMessages] Failed to load conversations:', error);
      } finally {
        if (isMountedRef.current) {
          setIsConversationsLoading(false);
        }
      }
    },
    [searchQuery, currentUserKey, transformConversationsForDisplay, updateConversationsState]
  );

  const loadMoreMessagesAsync = useCallback(
    async (conversationId) => {
      // Guard conditions: prevent multiple simultaneous loads
      if (
        isMessagesLoadingMore ||
        isMessagesLoading ||
        conversationMessages.length === 0 ||
        !hasMoreMessages
      ) {
        return;
      }

      setIsMessagesLoadingMore(true);
      try {
        const nextPage = currentMessagePage + 1;
        await loadMessagesAsync(conversationId, nextPage);
      } catch (error) {
        console.error('[DirectMessages] Failed to load more messages:', error);
      } finally {
        if (isMountedRef.current) {
          setIsMessagesLoadingMore(false);
        }
      }
    },
    [
      isMessagesLoadingMore,
      isMessagesLoading,
      conversationMessages.length,
      hasMoreMessages,
      currentMessagePage,
      loadMessagesAsync,
    ]
  );

  const closeConversation = useCallback(() => {
    setSelectedConversation(null);
    setConversationMessages([]);
    setFeedSettings([]);
    setEditorProps(null);
    setCurrentMessagePage(1);
    setHasMoreMessages(false);
    setShowNewMessagesIndicator(false);
    setIsAtBottom(true);
    if (onBackToOriginalTab) {
      onBackToOriginalTab();
    }
    // Only reload if user sent messages AND there's no active search (preserve search results)
    if (hasSentMessageInSessionRef.current && !searchQuery) {
      loadConversations(1, true);
      hasSentMessageInSessionRef.current = false;
    }

  }, [onBackToOriginalTab, searchQuery, loadConversations]);

  // Socket event handlers
  const handleNewConversationCreated = useCallback(
    (socketData) => {
      if (searchQuery !== '') return;

      const feedType = socketData?.feed_type || socketData?.metadata?.feed_type;
      if (feedType?.toUpperCase() !== FEED_TYPE.DM) return;
      const newConversation = transformSocketEventToConversation(socketData, currentUserKey, false);
      const conversationWithHandlers = {
        ...newConversation,
        onFeedSelectHandler: openConversation,
        onViewFullHandler: openConversation,
      };

      setConversations((prev) => [conversationWithHandlers, ...prev]);
      setTotalConversationCount((prev) => prev + 1);
    },
    [searchQuery, currentUserKey, openConversation]
  );

  const handleNewMessageInConversation = useCallback(
    (socketData) => {
      if (searchQuery !== '') return;

      const feedType = socketData?.feed_type || socketData?.metadata?.feed_type;
      if (feedType?.toUpperCase() !== FEED_TYPE.DM) return;

      const conversationId = String(socketData.feed_id || '');
      const isCurrentConversation = selectedConversationRef.current?.id != null && String(selectedConversationRef.current.id) === conversationId;

      if (isCurrentConversation) {
        const newMessage = transformSocketEventToMessage(socketData, currentUserKey, menuProps);
        // Check if the message is from the current user (sent by them)
        const isFromCurrentUser = newMessage.userId === currentUserKey;
        
        addMessageToCurrentConversation(newMessage);
        markConversationAsRead(conversationId);
        
        // Only auto-scroll if user is at bottom or if it's their own message
        // If user is scrolled up and message is from someone else, show indicator
        // The component will handle scroll detection and call onNewMessagesIndicatorDismiss when user scrolls to bottom
        if (isFromCurrentUser) {
          // User's own message - always scroll to bottom (if not loading more)
          if (!isMessagesLoadingMore) {
            messageViewRef?.current?.scrollToBottom();
            setIsAtBottom(true);
            setShowNewMessagesIndicator(false);
          }
        } else if (isAtBottom && !isMessagesLoadingMore) {
          // User is at bottom and not loading more - scroll to show new message
          messageViewRef?.current?.scrollToBottom();
          setIsAtBottom(true);
          setShowNewMessagesIndicator(false);
        } else {
          // User is scrolled up or loading more messages - show indicator
          setIsAtBottom(false);
          setShowNewMessagesIndicator(true);
        }
      }
      else {
        setConversations((prev) => {
          const existingConversation = prev.find((conv) => conv.id === conversationId);

          if (existingConversation) {
            const updatedConversation = updateExistingConversation(existingConversation, socketData, isCurrentConversation);
            return [updatedConversation, ...prev.filter((conv) => conv.id !== conversationId)];
          } else {
            const newConversation = transformSocketEventToConversation(socketData, currentUserKey, isCurrentConversation);
            newConversation.onFeedSelectHandler = openConversation;
            return [newConversation, ...prev];
          }
        });

      }
    },
    [searchQuery, currentUserKey, isAtBottom, isMessagesLoadingMore, addMessageToCurrentConversation, updateExistingConversation, menuProps, openConversation]
  );

  // UI event handlers
  const handleSearchChange = useCallback((event) => {
    setIsConversationsLoading(true);
    setTotalConversationCount(0);
    setConversations([]);
    setCurrentPage(1);
    setSearchQuery(event.target.value);
  }, [setSearchQuery, setCurrentPage, setIsConversationsLoading, setTotalConversationCount, setConversations]);

  const loadMoreConversations = useCallback(() => {
    if (hasMore && !isConversationsLoading) {
      setCurrentPage((currentPage) => {
        const newPage = currentPage + 1
        if (isActive && hasLoadedInitialConversations && currentPage > 1 && !selectedConversation) {
          loadConversations(newPage, false);
        }
        return newPage
      });
    }
  }, [hasMore, isActive, hasLoadedInitialConversations, isConversationsLoading, selectedConversation]);


  // Memoize the new message handler to prevent unnecessary re-renders
  const handleNewMessage = useCallback(() => {
  }, []);

  // Handle when indicator should be dismissed
  // Called by the component when user scrolls to bottom or clicks the indicator
  const handleIndicatorDismiss = useCallback(() => {
    setShowNewMessagesIndicator(false);
    setIsAtBottom(true);
  }, []);

  // Effects

  useEffect(() => {
    // Close conversation when switching away from Messages tab
    // Only close if activeTab is explicitly set and not MESSAGES
    if (activeTab !== undefined && activeTab !== TAB_NAMES.MESSAGES && selectedConversation) {
      closeConversation();
    }
  }, [activeTab, selectedConversation, closeConversation]);
  
  useEffect(() => {
    clearTimeout(searchDebounceTimer.current);
    searchDebounceTimer.current = setTimeout(() => {
      loadConversations(1, true);
    }, 1000);
  }, [
    searchQuery
  ]);

  useEffect(() => {
    conversationListRef.current = conversations;
  }, [conversations]);

  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);


  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    }
  }, []);

  // Load initial conversations when tab becomes active
  useEffect(() => {
    // Only load conversations if:
    // 1. Tab is active
    // 2. We haven't loaded initial conversations yet
    // 3. We're not currently loading
    // 4. We don't have any conversations already (to prevent reloading when opening conversations)
    // 5. No conversation is currently selected (to prevent reloading when opening conversations)
    if (isActive && !hasLoadedInitialConversations && !isConversationsLoading && conversations.length === 0 && !selectedConversation) {
      loadConversations(1, true);
    }
  }, [isActive, hasLoadedInitialConversations, isConversationsLoading, conversations.length, selectedConversation, loadConversations]);


  // Memoized values

  const showLoadMore = useMemo(() => {
    return hasMore && !isConversationsLoading;
  }, [hasMore, isConversationsLoading]);

  // Now we don't need finalEditorProps - just use editorProps directly
  const userProps = useMemo(() => {
    return {
      userId: currentUserKey,
      firstName: userDetail.firstName,
      lastName: userDetail.lastName,
    };
  }, [currentUserKey, userDetail.firstName, userDetail.lastName]);


  const getUnreadCountsByConversationId = (conversationId) => {
    const unreadCount = conversationListRef.current.find((conv) => conv.id === conversationId)?.unreadCount || 0
    return unreadCount;
  }
  const messageView = useMemo(() => {
    if (!selectedConversation) {
      return undefined;
    }
    return {
      isLoading: isMessagesLoading,
      messages: conversationMessages,
      selectedFeed: {
        ...selectedConversation,
        settings: feedSettings,
      },
      showLoadMore: showLoadMore,
      userProps,
      onBackHandler: closeConversation,
      onMessageSendHandler: (feed, msgHTML) => sendMessage(msgHTML),
      onSendHandler: sendMessage,
      editorProps: editorProps,
      showSettingsIcon: true,
      onLoadMoreHandler: () => loadMoreMessagesAsync(selectedConversation.id),
      showNewMessagesIndicator: showNewMessagesIndicator,
      onNewMessagesIndicatorDismiss: handleIndicatorDismiss,
      hasMoreMessages: hasMoreMessages
    };
  }, [
    selectedConversation,
    isMessagesLoading,
    conversationMessages,
    userProps,
    editorProps,
    feedSettings,
    closeConversation,
    sendMessage,
    hasMoreMessages,
    loadMoreMessagesAsync,
    showNewMessagesIndicator,
    handleIndicatorDismiss,
  ]);

  return {
    userDetail,
    searchQuery,
    feedDetails,
    conversations,
    isLoading: isConversationsLoading,
    totalConversationCount,
    messageView,
    messageViewRef,
    showLoadMore,
    feedSettings,
    showNewMessageBtn,
    loadConversations,
    openConversation,
    sendMessage,
    onSearchHandler: handleSearchChange,
    onLoadMore: loadMoreConversations,
    onNewMessageHandler: handleNewMessage,
    setConversations,
    handleFeedDetailsLoaded,
    // Expose refs for useFeedsManager
    selectedConversationRef,
    refreshFeedDetails,
    // Socket event handlers (called from useFeedsManager)
    onConversationCreated: handleNewConversationCreated,
    onMessageInConversation: handleNewMessageInConversation,
    // Modal state
    readReceiptsModal,
    menuProps,
  };
};
